package fr.frux.feature.map.site

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.ChevronRight
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import fr.frux.core.designsystem.components.InterceptNavigationBack
import fr.frux.core.designsystem.components.LocalBottomNavPadding
import fr.frux.core.designsystem.format.formatDistance
import fr.frux.core.designsystem.icons.FruxSpeciesGlyphs
import fr.frux.core.designsystem.icons.glyphFor
import fr.frux.core.designsystem.icons.hybridMarkerTint
import fr.frux.core.designsystem.theme.FruxSpacing
import fr.frux.core.designsystem.theme.FruxStatus
import fr.frux.core.designsystem.theme.fruxColors
import fr.frux.feature.map.resources.Res
import fr.frux.feature.map.resources.map_site_distance
import fr.frux.feature.map.resources.map_site_open_description
import fr.frux.feature.map.resources.map_site_rule_ask
import fr.frux.feature.map.resources.map_site_rule_open
import fr.frux.feature.map.resources.map_site_rule_private
import fr.frux.feature.map.resources.map_site_rule_private_harvest
import fr.frux.feature.map.resources.map_site_species_more
import fr.frux.feature.map.resources.map_site_species_none
import fr.frux.feature.map.resources.map_site_species_two
import fr.frux.feature.map.resources.map_site_summary
import fr.frux.feature.map.resources.map_site_summary_no_distance
import fr.frux.feature.map.speciesFamilyColor
import fr.frux.shared.domain.model.Site
import fr.frux.shared.domain.model.SiteAccessRule
import fr.frux.shared.domain.model.dominantPlanting
import fr.frux.shared.domain.model.isLocked
import fr.frux.shared.domain.model.liveNextHarvest
import fr.frux.shared.domain.model.orderedPlantings
import org.jetbrains.compose.resources.pluralStringResource
import org.jetbrains.compose.resources.stringResource

private val TILE_SIZE = 44.dp
private val TILE_CORNER = 12.dp
private val CARD_CORNER = 20.dp
private val GLYPH_SIZE = 24.dp

/**
 * Resume du lieu selectionne, pose dans le slot bas de la carte comme la fiche d'arbre mais en carte
 * flottante au-dessus de la barre de navigation (artboard « Main »). Toute la carte ouvre la fiche
 * du lieu. Un lieu prive ne dit ni espece ni effectif : la regle et la distance seulement.
 *
 * Le retour systeme ferme la selection comme sur la fiche d'arbre, avant de toucher a la pile.
 */
@Composable
internal fun SiteSummaryCard(
    site: Site,
    distanceMeters: Double?,
    nowMillis: Long,
    onOpen: () -> Unit,
    onClose: () -> Unit,
    modifier: Modifier = Modifier,
) {
    InterceptNavigationBack(onBack = onClose)
    val openDescription = stringResource(Res.string.map_site_open_description, site.name)
    Surface(
        onClick = onOpen,
        modifier =
            modifier
                .fillMaxWidth()
                .padding(horizontal = FruxSpacing.md)
                .padding(bottom = LocalBottomNavPadding.current + FruxSpacing.sm),
        shape = RoundedCornerShape(CARD_CORNER),
        color = MaterialTheme.colorScheme.surface,
        shadowElevation = 6.dp,
    ) {
        Row(
            modifier = Modifier.padding(horizontal = FruxSpacing.md, vertical = FruxSpacing.sm + FruxSpacing.xs),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            SiteTile(site, locked = site.isLocked(nowMillis))
            Spacer(modifier = Modifier.width(FruxSpacing.sm))
            Column(
                modifier = Modifier.weight(1f),
                verticalArrangement = Arrangement.spacedBy(2.dp),
            ) {
                Text(
                    text = site.name,
                    style = MaterialTheme.typography.titleMedium,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
                Text(
                    text = siteSummaryLine(site, distanceMeters, nowMillis),
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    maxLines = 2,
                    overflow = TextOverflow.Ellipsis,
                )
            }
            Icon(
                imageVector = Icons.Outlined.ChevronRight,
                contentDescription = openDescription,
                tint = MaterialTheme.colorScheme.onSurfaceVariant,
                modifier = Modifier.size(GLYPH_SIZE),
            )
        }
    }
}

/** Plaque carree du lieu : la teinte et le glyphe de la plaque de la carte, cadenas pour un lieu verrouille. */
@Composable
private fun SiteTile(
    site: Site,
    locked: Boolean,
) {
    val colors = MaterialTheme.fruxColors
    val (tint: Color, glyph: ImageVector) =
        if (locked) {
            colors.siteLocked to FruxSpeciesGlyphs.Cadenas
        } else {
            val dominant = site.dominantPlanting()?.species
            val family = speciesFamilyColor(dominant?.scientificName.orEmpty(), null, colors)
            val icon = dominant?.let { glyphFor(it.scientificName, null) } ?: FruxSpeciesGlyphs.Arbre
            hybridMarkerTint(family, FruxStatus.Available, colors) to icon
        }
    Box(
        modifier =
            Modifier
                .size(TILE_SIZE)
                .clip(RoundedCornerShape(TILE_CORNER))
                .background(tint),
        contentAlignment = Alignment.Center,
    ) {
        Icon(
            imageVector = glyph,
            contentDescription = null,
            tint = Color.White,
            modifier = Modifier.size(GLYPH_SIZE),
        )
    }
}

/** « Especes · regle · distance », distance omise quand la position est inconnue. */
@Composable
private fun siteSummaryLine(
    site: Site,
    distanceMeters: Double?,
    nowMillis: Long,
): String {
    // Privé mais déverrouillé par une récolte : la plaque de la carte porte la date, la carte aussi.
    val harvest = site.liveNextHarvest(nowMillis)
    val rule =
        when {
            site.accessRule == SiteAccessRule.OPEN -> stringResource(Res.string.map_site_rule_open)
            site.accessRule == SiteAccessRule.ASK -> stringResource(Res.string.map_site_rule_ask)
            harvest != null -> stringResource(Res.string.map_site_rule_private_harvest, harvest.badgeLabel())
            else -> stringResource(Res.string.map_site_rule_private)
        }
    val distance = distanceMeters?.let { stringResource(Res.string.map_site_distance, formatDistance(it)) }
    val species = if (site.accessRule == SiteAccessRule.PRIVATE) null else siteSpeciesText(site)
    return when {
        species != null && distance != null -> stringResource(Res.string.map_site_summary, species, rule, distance)
        species != null -> stringResource(Res.string.map_site_summary_no_distance, species, rule)
        distance != null -> stringResource(Res.string.map_site_summary_no_distance, rule, distance)
        else -> rule
    }
}

/** « Pommier », « Pommier et noisetier », « Pommier, noisetier et 1 autre » : les plus nombreux d'abord. */
@Composable
private fun siteSpeciesText(site: Site): String {
    val names = site.orderedPlantings().map { it.species.commonNameFr }
    return when (names.size) {
        0 -> {
            stringResource(Res.string.map_site_species_none)
        }

        1 -> {
            names[0]
        }

        2 -> {
            stringResource(Res.string.map_site_species_two, names[0], names[1].lowercase())
        }

        else -> {
            pluralStringResource(
                Res.plurals.map_site_species_more,
                names.size - 2,
                names[0],
                names[1].lowercase(),
                names.size - 2,
            )
        }
    }
}
